import ScrubstrApiQueryService from '../ScrubstrApiQueryService/ScrubstrApiQueryService'
import { Notification, NotificationMetadata, NotificationResponse } from '../../models/Notification.model'

export default class NotificationService {
  private apiService: ScrubstrApiQueryService

  constructor() {
    this.apiService = new ScrubstrApiQueryService()
  }

  /**
   * Fetch all notifications for the current user
   */
  async getNotifications(): Promise<NotificationResponse> {
    const response = await this.apiService.get<Notification[] | NotificationResponse>({
      endpoint: '/notifications',
      params: {
        limit: 50,
        unreadOnly: 'false',
      },
    })

    // Handle both array response and object response formats
    // API returns array directly: [Notification, Notification, ...]
    // Or object format: { notifications: [...], unreadCount: number }
    let notifications: Notification[] = []
    let unreadCount = 0

    if (Array.isArray(response)) {
      // Direct array response
      notifications = response
      unreadCount = notifications.filter((n) => !n.read).length
    } else if (response && typeof response === 'object' && 'notifications' in response) {
      // Object response with notifications property
      notifications = (response as NotificationResponse).notifications || []
      unreadCount = (response as NotificationResponse).unreadCount || notifications.filter((n) => !n.read).length
    }

    return {
      notifications,
      unreadCount,
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string | number): Promise<void> {
    return this.apiService.post<void>({
      endpoint: `/notifications/${notificationId}/read`,
      data: {},
    })
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    return this.apiService.post<void>({
      endpoint: '/notifications/read-all',
      data: {},
    })
  }

  /**
   * Send (create) a new notification
   */
  async sendNotification(data: {
    title: string
    message: string
    type: string
    metadata?: NotificationMetadata
  }): Promise<void> {
    return this.apiService.post<void>({
      endpoint: '/notifications',
      data,
    })
  }
}
